<template>
    <SeoHead :overrides="seoOverrides" />

    <HeaderRenderer :menu="page.props.menu ?? undefined" :slot-data="prismicPage?.header" />

    <main>
        <BodyRenderer :page-type="pageType" :slices="prismicPage?.body ?? []" />
        <Container v-if="isHomepage"><TrustedShopsWidget location="home" /></Container>
    </main>

    <FooterRenderer :slot-data="prismicPage?.footer" />

    <AppToaster />
</template>

<script lang="ts" setup>
import Container from '@/components/layouts/Container.vue';
import BodyRenderer from '@/components/prismic/BodyRenderer.vue';
import FooterRenderer from '@/components/prismic/FooterRenderer.vue';
import HeaderRenderer from '@/components/prismic/HeaderRenderer.vue';
import { toPascalCase } from '@/components/prismic/SliceRegistry';
import TrustedShopsWidget from '@/components/reviews/TrustedShopsWidget.vue';
import SeoHead from '@/components/seo/SeoHead.vue';
import AppToaster from '@/components/toasters/AppToaster.vue';
import { useLipscoreWidgetScript } from '@/composables/useLipscoreWidgetScript';
import { pageIsHomepage } from '@/lib/pageView';
import type { AppPageProps } from '@/types';
import { PrismicPage } from '@/types/models/PrismicPageData';
import type { SeoOverrides } from '@/types/seo';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

useLipscoreWidgetScript();

const page = usePage<AppPageProps<{ prismic: PrismicPage }>>();
const prismicPage = computed((): PrismicPage | undefined => page.props.prismic);

const isHomepage = computed((): boolean => pageIsHomepage(page));

const pageType = computed((): string | undefined => (prismicPage.value?.type ? toPascalCase(prismicPage.value.type) : undefined));

const cleanPageTitle = computed((): string => {
    const title: string | undefined = prismicPage.value?.page_title ?? (prismicPage.value?.header_title as string | undefined);

    if (!title || typeof title !== 'string') {
        console.info(title);
        return '';
    }

    return title
        .replace(/<h1>/g, '')
        .replace(/<\/h1>/g, '')
        .trim();
});

// `store` is optional-chained because AppServiceProvider also renders this component as the
// error page for 403/404/500/503, which can happen on requests without shared store data.
const pageTitle = computed((): string => prismicPage.value?.meta_title || cleanPageTitle.value || page.props.store?.store_group?.name || '');

const seoOverrides = computed((): SeoOverrides => ({
    title: pageTitle.value,
    description: prismicPage.value?.meta_description ?? null,
}));
</script>
